"use client"

import { useState, useEffect } from "react"
import Button from "@atoms/Button"
import { createPortal } from "react-dom"
import {
  BY_PHONE_PLACEHOLDER,
  formatPhoneAuto,
  validateInternationalPhone,
} from "@lib/phone"
import { PhoneInput } from "../atoms/PhoneInput"
import { getClientInfo } from "@lib/client-info"
import { extractEmailDomain, pushGtmEvent } from "@lib/gtm"
import { Checkbox } from "../atoms/Checkbox"

interface ContactModalProps {
  isOpen: boolean
  onClose: () => void
  source?: string
}

export type ModalValues = {
  name: string
  email: string
  phone: string
  message: string
  agree: boolean
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i

const validate = (values: ModalValues) => {
  const errors: Partial<Record<keyof ModalValues, string>> = {}
  if (!values.name || values.name.trim().length < 2) {
    errors.name = "Введите имя (минимум 2 символа)"
  }
  if (!values.email || !emailRegex.test(values.email)) {
    errors.email = "Укажите корректный email"
  }
  if (!values.phone || values.phone.trim().length < 0) {
    errors.phone = "Некорректный номер телефона"
  } else if (!validateInternationalPhone(values.phone).isValid) {
    errors.phone = "Некорректный номер телефона"
  }
  if (!values.message || values.message.trim().length < 5) {
    errors.message = "Опишите задачу хотя бы парой слов"
  }
  if (!values.agree) {
    errors.agree = "Необходимо согласие на обработку данных"
  }
  return errors
}

const ContactModal = ({
  isOpen,
  onClose,
  source = "contact_modal",
}: ContactModalProps) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    agree: false,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle")

  const [errors, setErrors] = useState<Partial<Record<keyof ModalValues, string>>>({})

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }

    if (isOpen) {
      document.addEventListener("keydown", handleEscape)
      document.body.style.overflow = "hidden"
    }

    return () => {
      document.removeEventListener("keydown", handleEscape)
      document.body.style.overflow = "unset"
    }
  }, [isOpen, onClose])

  const handlePhoneChange = (value: string) => {
    setFormData((prev) => ({ ...prev, phone: value }))
    // Очищаем ошибку при изменении номера
    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: "" }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const formValues = { ...formData }
    const nextErrors = validate(formValues)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    setIsSubmitting(true)

    try {
      // Собираем техническую информацию и UTM метки
      const clientInfo = await getClientInfo()

      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          source,
          page: typeof window !== "undefined" ? window.location.href : undefined,
          ...clientInfo,
        }),
      })

      if (!response.ok) {
        throw new Error("Failed to send message")
      }

      setSubmitStatus("success")
      pushGtmEvent("lead_submit", {
        formId: "contact-modal",
        source,
        page: typeof window !== "undefined" ? window.location.href : undefined,
        emailDomain: extractEmailDomain(formData.email),
        hasPhone: Boolean(formData.phone.trim()),
      })

      // Сброс формы через 2 секунды
      setTimeout(() => {
        setFormData({
          name: "",
          email: "",
          phone: "",
          message: "",
          agree: false,
        })
        setSubmitStatus("idle")
        onClose()
      }, 2000)
    } catch (error) {
      console.error("Error sending contact form:", error)
      setSubmitStatus("error")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement
    if (name === "phone") {
      const formatted = formatPhoneAuto(value)
      setFormData((prev) => ({ ...prev, phone: formatted }))
      return
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-xs" onClick={onClose} />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-modal-title"
        className="relative  bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center text-white justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-linear-to-r from-brand-500 to-brand-600 dark:from-jungle-500 dark:to-jungle-600">
          <h2 id="contact-modal-title" className="text-title">
            Ранний доступ к WEBDAD OS
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Закрыть"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form noValidate onSubmit={handleSubmit} className="p-6 space-y-4">
          {submitStatus === "success" ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-green-600 dark:text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-subhead text-gray-900 dark:text-white mb-2">
                Спасибо, получили!
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Скоро свяжемся и договоримся о первом шаге.
              </p>
            </div>
          ) : (
            <>
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Имя
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 dark:focus-visible:ring-jungle-500 focus-visible:border-transparent"
                  placeholder="Ваше имя"
                />
                {errors.name ? (
                  <p className="text-sm text-red-500 mt-1">{errors.name}</p>
                ) : null}
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 dark:focus-visible:ring-jungle-500 focus-visible:border-transparent"
                  placeholder="your@email.com"
                />
                {errors.email ? (
                  <p className="text-sm text-red-500 mt-1">{errors.email}</p>
                ) : null}
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Телефон
                </label>
                <PhoneInput
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  placeholder={BY_PHONE_PLACEHOLDER}
                  variant="glass"
                  isInvalid={Boolean(errors.phone)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 dark:focus-visible:ring-jungle-500 focus-visible:border-transparent"
                />
                {errors.phone && (
                  <p className="text-sm text-red-500 mt-1">{errors.phone}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Что хотите автоматизировать или изменить?
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 dark:focus-visible:ring-jungle-500 focus-visible:border-transparent resize-none"
                  placeholder="Например: заявки из Instagram теряются, отчёт по продажам собираем вручную"
                />
                {errors.message ? (
                  <p className="text-sm text-red-500 mt-1">{errors.message}</p>
                ) : null}
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="agree"
                  name="agree"
                  checked={formData.agree}
                  onChange={handleChange}
                  aria-invalid={Boolean(errors.agree) || undefined}
                  isInvalid={Boolean(errors.agree)}
                  className="mt-0.5 shrink-0 accent-brand-500 dark:accent-jungle-500"
                />
                <div>
                  <label
                    htmlFor="agree"
                    className={
                      "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    }
                  >
                    Я соглашаюсь на обработку персональных данных и принимаю условия
                    политики конфиденциальности.
                  </label>
                </div>
              </div>
              {errors.agree ? (
                <span className={"text-sm text-red-500 mt-0 mb-0"}>{errors.agree}</span>
              ) : null}

              {submitStatus === "error" && (
                <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">
                    Произошла ошибка при отправке. Попробуйте еще раз.
                  </p>
                </div>
              )}

              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                  size="lg"
                  variant="gradientPill"
                  className="w-full disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isSubmitting ? "Отправка..." : "Отправить"}
                </Button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>,
    document.body,
  )
}

export default ContactModal
